import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, Search, LockOpen } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { matchesQuery } from "../lib/search";
import { useAuth } from "../context/AuthContext";
import { useSortable } from "../lib/sortable";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { GT_BANKS, BANK_ACCOUNT_TYPES } from "../lib/banks";
import { toast } from "sonner";

const empty = { username: "", password: "", name: "", email: "", phone: "", role_id: "", role_ids: [], active: true,
  employee_no: "", id_number: "", nit: "", birthdate: "", address: "", position: "", department: "", boss: "",
  hire_date: "", contract_type: "", employment_status: "Activo", blood_type: "", emergency_contact: "",
  emergency_relation: "", emergency_phone: "", photo_url: "", signature_url: "", dpi_front_url: "", dpi_back_url: "",
  contract_url: "", salary: "", bank_name: "", bank_account: "", igss_affiliation: "" };

export default function UsersPage() {
  const { Th, sortRows } = useSortable();
  const { can, user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [uploading, setUploading] = useState("");
  const [cleanOpen, setCleanOpen] = useState(false);
  const [cleanWord, setCleanWord] = useState("");
  const [cleaning, setCleaning] = useState(false);
  const [cleanResult, setCleanResult] = useState(null);

  const fileUrl = (u) => !u ? "" : (u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}?auth=${localStorage.getItem("selec_token") || ""}`);
  const uploadTo = async (key, file) => {
    if (!file) return;
    setUploading(key);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, [key]: r.data.url }));
      toast.success("Archivo subido");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUploading(""); }
  };
  const fld = (key, label, type = "text") => (
    <div className="space-y-1.5">
      <Label className="text-xs font-bold uppercase tracking-widest">{label}</Label>
      <Input type={type} value={form[key] ?? ""} onChange={(e) => setForm({ ...form, [key]: e.target.value })} className="rounded-sm" data-testid={`form-${key}`} />
    </div>
  );
  const fileFld = (key, label) => (
    <div className="space-y-1.5">
      <Label className="text-xs font-bold uppercase tracking-widest">{label}</Label>
      <div className="flex items-center gap-2">
        <Input type="file" accept="image/*,application/pdf" disabled={uploading === key} onChange={(e) => uploadTo(key, e.target.files?.[0])} className="rounded-sm text-xs" data-testid={`upload-${key}`} />
        {uploading === key ? <Loader2 className="h-4 w-4 animate-spin shrink-0" /> : form[key] ? <a href={fileUrl(form[key])} target="_blank" rel="noreferrer" className="text-xs text-primary underline whitespace-nowrap">Ver</a> : null}
      </div>
    </div>
  );

  const load = async () => {
    setLoading(true);
    try {
      const [u, r] = await Promise.all([api.get("/users"), api.get("/roles")]);
      setUsers(u.data);
      setRoles(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm({ ...empty, role_id: roles[0]?.id || "" }); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ ...empty, ...u, role_ids: u.role_ids || [], password: "" }); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      const collab = ["employee_no", "id_number", "nit", "birthdate", "address", "position", "department", "boss", "hire_date", "contract_type", "employment_status", "blood_type", "emergency_contact", "emergency_relation", "emergency_phone", "photo_url", "signature_url", "dpi_front_url", "dpi_back_url", "contract_url", "bank_name", "bank_account", "bank_account_type", "igss_affiliation"];
      const extra = {}; collab.forEach((k) => { extra[k] = form[k] ?? ""; });
      extra.salary = (form.salary === "" || form.salary === null || form.salary === undefined) ? null : Number(form.salary);
      const roleIds = (form.role_ids || []).filter((r) => r !== form.role_id);
      if (editing) {
        const payload = { name: form.name, email: form.email, phone: form.phone, role_id: form.role_id, role_ids: roleIds, active: form.active, ...extra };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing.id}`, payload);
        toast.success("Usuario actualizado");
      } else {
        await api.post("/users", { ...form, role_ids: roleIds, salary: extra.salary });
        toast.success("Usuario creado");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/users/${delTarget.id}`);
      toast.success("Usuario eliminado");
      setDelTarget(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doUnlock = async (u) => {
    try {
      await api.post(`/users/${u.id}/unlock`);
      toast.success(`${u.name} desbloqueado`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doCleanup = async () => {
    setCleaning(true);
    try {
      const r = await api.post("/admin/cleanup-test-data", { confirm: cleanWord });
      setCleanResult(r.data.detail);
      toast.success("Limpieza completada");
      setCleanWord("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setCleaning(false); }
  };

  const filtered = users.filter((u) =>
    matchesQuery([u.name, u.username, u.role_name, u.email], q)
  );

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="user-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar usuario..." className="pl-9 rounded-sm" />
        </div>
        {can("users:create") && (
          <Button onClick={openCreate} data-testid="new-user-btn" className="rounded-sm gap-2 font-bold uppercase tracking-wider text-xs">
            <Plus className="h-4 w-4" /> Nuevo usuario
          </Button>
        )}
      </div>

      <Card className="rounded-sm border shadow-none overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="users-table">
              <thead>
                <tr className="bg-muted text-left text-xs uppercase tracking-widest text-muted-foreground">
                  <Th field="name">Nombre</Th>
                  <Th field="username">Usuario</Th>
                  <Th field="role_name">Rol</Th>
                  <Th field="email">Contacto</Th>
                  <Th field="active">Estado</Th>
                  <th className="px-4 py-3 font-bold text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {sortRows(filtered).map((u) => (
                  <tr key={u.id} className="border-t border-border hover:bg-muted/50 transition-colors" data-testid={`user-row-${u.username}`}>
                    <td className="px-4 py-2.5 font-medium">{u.name}</td>
                    <td className="px-4 py-2.5 font-mono text-muted-foreground">@{u.username}</td>
                    <td className="px-4 py-2.5">{u.role_name}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{u.email || u.phone || "—"}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-1.5">
                        <span className={`inline-block px-2 py-0.5 rounded-sm text-[11px] font-bold uppercase ${u.active ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>
                          {u.active ? "Activo" : "Inactivo"}
                        </span>
                        {u.locked && (
                          <span className="inline-block px-2 py-0.5 rounded-sm text-[11px] font-bold uppercase bg-destructive/15 text-destructive" data-testid={`locked-badge-${u.username}`}>Bloqueado</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      {can("users:edit") && u.locked && (
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-amber-600" title="Desbloquear por reintentos" onClick={() => doUnlock(u)} data-testid={`unlock-user-${u.username}`}>
                          <LockOpen className="h-4 w-4" />
                        </Button>
                      )}
                      {can("users:edit") && (
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(u)} data-testid={`edit-user-${u.username}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                      {can("users:delete") && u.id !== me.id && (
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => setDelTarget(u)} data-testid={`delete-user-${u.username}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">Sin usuarios.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Form dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Editar usuario" : "Nuevo usuario"}</DialogTitle>
            <DialogDescription>Asigna los datos y el rol del usuario.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-widest">Nombre completo</Label>
              <Input data-testid="form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-widest">Usuario</Label>
              <Input data-testid="form-username" value={form.username} disabled={!!editing} onChange={(e) => setForm({ ...form, username: e.target.value })} className="rounded-sm" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-widest">{editing ? "Nueva contraseña" : "Contraseña"}</Label>
              <Input data-testid="form-password" type="password" placeholder={editing ? "Dejar vacío para no cambiar" : ""} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-sm" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-widest">Rol principal</Label>
              <Select value={form.role_id} onValueChange={(v) => setForm({ ...form, role_id: v, role_ids: (form.role_ids || []).filter((r) => r !== v) })}>
                <SelectTrigger className="rounded-sm" data-testid="form-role"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                <SelectContent>
                  {roles.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-widest">Correo</Label>
              <Input data-testid="form-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-sm" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-widest">Teléfono</Label>
              <Input data-testid="form-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-sm" />
            </div>
          </div>
          <div className="pt-3 mt-1 border-t"><p className="text-xs font-bold uppercase tracking-widest text-primary mb-2">Identificación</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {fld("employee_no", "N.º de colaborador")}
              {fld("id_number", "DPI / CUI")}
              {fld("nit", "NIT")}
              {fld("birthdate", "Fecha de nacimiento", "date")}
              {fld("blood_type", "Tipo de sangre")}
              <div className="sm:col-span-2">{fld("address", "Dirección de residencia")}</div>
            </div>
          </div>
          <div className="pt-3 border-t"><p className="text-xs font-bold uppercase tracking-widest text-primary mb-2">Datos laborales</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {fld("position", "Puesto / cargo")}
              {fld("department", "Departamento / área")}
              {fld("boss", "Jefe inmediato")}
              {fld("hire_date", "Fecha de ingreso", "date")}
              {fld("contract_type", "Tipo de contrato")}
              {fld("employment_status", "Situación laboral")}
            </div>
          </div>
          {can("users:payroll_info") && <div className="pt-3 border-t"><p className="text-xs font-bold uppercase tracking-widest text-amber-600 mb-2">Nómina (confidencial)</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {fld("salary", "Salario base", "number")}
              <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-widest">Banco</Label>
                <Select value={form.bank_name === "Otro" || (form.bank_name && !GT_BANKS.includes(form.bank_name)) ? "Otro" : (form.bank_name || "")} onValueChange={(v) => setForm({ ...form, bank_name: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="user-bank"><SelectValue placeholder="Selecciona banco" /></SelectTrigger>
                  <SelectContent>{GT_BANKS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent></Select>
                {(form.bank_name === "Otro" || (form.bank_name && !GT_BANKS.includes(form.bank_name))) && <Input className="rounded-sm mt-1" placeholder="Nombre del banco" value={form.bank_name === "Otro" ? "" : form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} data-testid="user-bank-other" />}</div>
              <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-widest">Tipo de cuenta</Label>
                <Select value={form.bank_account_type || ""} onValueChange={(v) => setForm({ ...form, bank_account_type: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="user-account-type"><SelectValue placeholder="Monetaria / Ahorro" /></SelectTrigger>
                  <SelectContent>{BANK_ACCOUNT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
              {fld("bank_account", "N.º de cuenta")}
              {fld("igss_affiliation", "Afiliación IGSS")}
            </div>
          </div>}
          <div className="pt-3 border-t"><p className="text-xs font-bold uppercase tracking-widest text-primary mb-2">Contacto de emergencia</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {fld("emergency_contact", "Nombre")}
              {fld("emergency_relation", "Parentesco")}
              {fld("emergency_phone", "Teléfono")}
            </div>
          </div>
          <div className="pt-3 border-t"><p className="text-xs font-bold uppercase tracking-widest text-primary mb-2">Documentos</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {fileFld("photo_url", "Fotografía")}
              {fileFld("dpi_front_url", "DPI (frente)")}
              {fileFld("dpi_back_url", "DPI (reverso)")}
              {fileFld("contract_url", "Contrato")}
            </div>
          </div>
          <div className="space-y-2 pt-3 border-t">
            <Label className="text-xs font-bold uppercase tracking-widest">Perfiles adicionales</Label>
            <p className="text-[11px] text-muted-foreground">Roles extra que este usuario podrá activar desde su menú. El rol principal siempre está disponible.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-40 overflow-y-auto rounded-sm border p-2" data-testid="extra-roles">
              {roles.filter((r) => r.id !== form.role_id).map((r) => {
                const checked = (form.role_ids || []).includes(r.id);
                return (
                  <label key={r.id} className="flex items-center gap-2 text-sm cursor-pointer px-1.5 py-1 rounded hover:bg-muted" data-testid={`extra-role-${r.id}`}>
                    <input
                      type="checkbox"
                      className="accent-primary h-4 w-4"
                      checked={checked}
                      onChange={(e) => {
                        const set = new Set(form.role_ids || []);
                        if (e.target.checked) set.add(r.id); else set.delete(r.id);
                        setForm({ ...form, role_ids: [...set] });
                      }}
                    />
                    <span>{r.name}</span>
                  </label>
                );
              })}
              {roles.filter((r) => r.id !== form.role_id).length === 0 && (
                <p className="text-xs text-muted-foreground col-span-full">No hay otros roles.</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3 pt-1">
            <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="form-active" />
            <Label className="text-sm">Usuario activo</Label>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button className="rounded-sm font-bold" onClick={save} disabled={saving || !form.name || !form.username || !form.role_id || (!editing && !form.password)} data-testid="save-user-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar usuario?</AlertDialogTitle>
            <AlertDialogDescription>Se eliminará <strong>{delTarget?.name}</strong> (@{delTarget?.username}). Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancelar</AlertDialogCancel>
            <AlertDialogAction className="rounded-sm bg-destructive hover:bg-destructive/90" onClick={doDelete} data-testid="confirm-delete-user">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {me?.is_admin && (
        <Card className="rounded-sm border-2 border-destructive/40 shadow-none p-5 mt-2" data-testid="danger-zone">
          <p className="text-xs font-bold uppercase tracking-widest text-destructive mb-1">Zona de peligro</p>
          <p className="text-sm text-muted-foreground mb-3">Limpieza de datos de prueba. Borra documentos comerciales, compras, tesorería, inventario y catálogos de prueba. <strong>Conserva</strong> usuarios, roles, configuración FEL/empresa, los correlativos y las facturas certificadas en <strong>Producción</strong> (SAT) con su cliente. Es <strong>irreversible</strong>.</p>
          <Button variant="destructive" className="rounded-sm gap-2 font-bold uppercase tracking-wider text-xs" onClick={() => { setCleanResult(null); setCleanWord(""); setCleanOpen(true); }} data-testid="open-cleanup-btn">
            <Trash2 className="h-4 w-4" /> Limpiar datos de prueba
          </Button>
        </Card>
      )}

      <Dialog open={cleanOpen} onOpenChange={(o) => { if (!cleaning) setCleanOpen(o); }}>
        <DialogContent className="rounded-sm max-w-lg" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="text-destructive">Limpiar datos de prueba</DialogTitle>
            <DialogDescription>Esta acción es IRREVERSIBLE. Se borrarán cotizaciones, pedidos, notas de envío, facturas de prueba, órdenes de compra, tesorería, inventario y catálogos (clientes/productos/proveedores/bancos) de prueba. Se conservan usuarios, roles, configuración y las facturas certificadas en Producción.</DialogDescription>
          </DialogHeader>
          {cleanResult ? (
            <div className="space-y-2">
              <p className="text-sm font-semibold text-green-700">✓ Limpieza completada.</p>
              <div className="text-xs font-mono bg-muted rounded-sm p-3 max-h-52 overflow-y-auto" data-testid="cleanup-result">
                {Object.entries(cleanResult).map(([k, v]) => <div key={k}>{k}: {v}</div>)}
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest">Escribe <span className="text-destructive">LIMPIAR</span> para confirmar</Label>
              <Input value={cleanWord} onChange={(e) => setCleanWord(e.target.value)} placeholder="LIMPIAR" className="rounded-sm" data-testid="cleanup-word" />
            </div>
          )}
          <DialogFooter>
            {cleanResult ? (
              <Button className="rounded-sm" onClick={() => { setCleanOpen(false); load(); }} data-testid="cleanup-close">Cerrar</Button>
            ) : (<>
              <Button variant="outline" className="rounded-sm" onClick={() => setCleanOpen(false)} disabled={cleaning}>Cancelar</Button>
              <Button variant="destructive" className="rounded-sm font-bold" onClick={doCleanup} disabled={cleaning || cleanWord !== "LIMPIAR"} data-testid="confirm-cleanup-btn">
                {cleaning ? "Limpiando..." : "Borrar definitivamente"}
              </Button>
            </>)}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
